import FormFieldCoordinator from './FormFieldCoordinator';
import { HekateMultiSelectOptionElement } from '@onshape/www-inertia';
import { ref, watch } from 'vue';

export default class MultiSelectFieldCoordinator extends FormFieldCoordinator {
  private selectedValues = ref<string[]>([]);

  private selectedOptions = ref<HekateMultiSelectOptionElement[]>([]);

  private optionList = ref<HekateMultiSelectOptionElement[]>([]);

  private parseSelectedValues(rawValue: unknown): string[] {
    if (Array.isArray(rawValue)) {
      return rawValue.map((value) => `${value}`.trim()).filter((value) => value.length > 0);
    }

    return `${rawValue || ''}`
      .split(',')
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }

  private getSelectedValues(): string[] {
    return [...this.selectedValues.value];
  }

  private buildOptionList(selectedValues = this.getSelectedValues()) {
    const { optionList } = this.field.value;

    return optionList.elements
      .filter((elm: { value: string }) => !!elm.value)
      .map((elm: { value: string; displayName: string; isOptGroup?: boolean | undefined }) => {
        return new HekateMultiSelectOptionElement({
          ...elm,
          isSelected: selectedValues.includes(elm.value),
        });
      });
  }

  private setSelectedValues(values: string[], emitModelUpdate = true) {
    const cleanedValues = [
      ...new Set(values.map((value) => value.trim()).filter((value) => value)),
    ];
    const optionList = this.buildOptionList(cleanedValues);

    this.selectedValues.value = cleanedValues;
    this.optionList.value = optionList;
    this.selectedOptions.value = optionList.filter((option) =>
      cleanedValues.includes(option.value)
    );
    if (emitModelUpdate) {
      this.context.emit('update:modelValue', cleanedValues.join(','));
    }
  }

  private selectOption({ value }: { value: string }) {
    const currentValues = this.getSelectedValues();
    this.setSelectedValues([...currentValues, value]);
  }

  private unselectOption({ value }: { value: string }) {
    const currentValues = this.getSelectedValues();
    this.setSelectedValues(currentValues.filter((currentValue) => currentValue !== value));
  }

  private toggleOption({ value }: { value: string }) {
    const currentValues = this.getSelectedValues();
    if (currentValues.includes(value)) {
      this.setSelectedValues(currentValues.filter((currentValue) => currentValue !== value));
      return;
    }

    this.setSelectedValues([...currentValues, value]);
  }

  private onSelectChange(evt: Event) {
    const target = evt.target as HTMLSelectElement | null;
    if (!target) return;

    const values = Array.from(target.selectedOptions).map((option) => option.value);
    this.setSelectedValues(values);
  }

  public setup() {
    this.setSelectedValues(this.parseSelectedValues(this.modelValue.value), false);

    watch(this.modelValue, (newValue) => {
      this.setSelectedValues(this.parseSelectedValues(newValue), false);
    });

    return {
      optionList: this.optionList,
      selectedOptions: this.selectedOptions,
      selectOption: this.selectOption.bind(this),
      unselectOption: this.unselectOption.bind(this),
      toggleOption: this.toggleOption.bind(this),
      onSelectChange: this.onSelectChange.bind(this),
    };
  }
}
